import React, { useState, useEffect } from 'react';
import { Award, Copy, Share2, RefreshCw, Check, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import API from '../lib/api';

const referralLevels = [
  { level: 1, percent: '15%' },
  { level: 2, percent: '5%' },
  { level: 3, percent: '3%' },
  { level: 4, percent: '2%' },
  { level: 5, percent: '1%' },
  { level: 6, percent: '1%' },
  { level: 7, percent: '1%' },
];

const Dashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState('rewards');
  const [copied, setCopied] = useState(false);
  const [commissions, setCommissions] = useState({ items: [], summary: [], total_earned: 0 });

  useEffect(() => {
    (async () => {
      try { const { data } = await API.get('/commissions/me'); setCommissions(data); } catch {}
    })();
  }, []);

  if (!user) return null;

  const sponsorLink = `${window.location.origin}/join?ref=${user.inviteCode}`;
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({ title: `${label} copied`, description: text });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast({ title: 'Copy failed', description: 'Please copy manually.' });
    }
  };

  return (
    <section className="px-4 md:px-10 py-10">
      <div className="mx-auto max-w-[1240px]">
        <div className="rounded-3xl bg-[#fbf4dc] border border-emerald-900/15 shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8 lg:p-10">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-serif text-[30px] md:text-[34px] text-emerald-950 tracking-tight">Portal Center</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-800/40 bg-emerald-200/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> Real Estate
              </span>
            </div>

            {/* Tabs */}
            <div className="mt-6 flex gap-2">
              <TabBtn active={tab === 'rewards'} onClick={() => setTab('rewards')}>Rewards</TabBtn>
              <TabBtn active={tab === 'structure'} onClick={() => setTab('structure')}>Structure</TabBtn>
            </div>

            {/* Panels */}
            <div className="mt-6">
              {tab === 'rewards' ? (
                <div className="space-y-6">
                  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
                    <div className="flex items-center gap-2">
                      <Award size={16} className="text-emerald-700" />
                      <span className="text-emerald-900 font-semibold text-[15px]">Referral Bonus Network</span>
                    </div>
                    <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70">Referral Slab Structure</div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                      {referralLevels.map((r) => (
                        <div key={r.level} className="rounded-xl bg-emerald-950/[0.04] border border-emerald-900/10 px-3 py-3 text-center">
                          <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-800/70">LVL {r.level}</div>
                          <div className="mt-1 font-serif text-emerald-950 text-xl">{r.percent}</div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-50/70 px-4 py-3 flex items-start gap-2.5" data-testid="referral-min-package-note">
                      <Award size={14} className="text-amber-700 mt-0.5 shrink-0" />
                      <div className="text-[12px] leading-snug text-amber-900/85">
                        <b>Eligibility:</b> To earn referral commissions, <b>you must hold a package of $100 or more yourself</b>. Once your own $100+ package is active (single-payment or installment), commissions from your entire downline start crediting to your Bonus wallet on every real installment they pay. Downlines whose own investment is under $100 will not generate commissions until they upgrade.
                      </div>
                    </div>

                    <div className="mt-6 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70">Received Referral Bonuses Log</div>
                    <div className="mt-3 rounded-xl bg-emerald-950/[0.03] border border-dashed border-emerald-900/15 px-4 py-6 text-center text-sm text-emerald-900/60">
                      No referral bonuses credited yet. Share your invitation link to build your referral network.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70">Your Invite Code</div>
                    <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      <div className="flex-1 font-serif text-2xl md:text-3xl text-emerald-950 tracking-wider">{user.inviteCode}</div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copy(user.inviteCode, 'Code')}
                          className="inline-flex items-center gap-2 rounded-xl border border-emerald-900/25 bg-white/60 hover:bg-white text-emerald-900 text-sm font-medium px-3.5 py-2.5 transition"
                        >
                          {copied ? <Check size={14} /> : <Copy size={14} />} Code
                        </button>
                        <button
                          onClick={() => copy(sponsorLink, 'Sponsor Link')}
                          className="inline-flex items-center gap-2 rounded-xl bg-emerald-950 hover:bg-emerald-900 text-emerald-50 text-sm font-medium px-3.5 py-2.5 transition"
                        >
                          <Share2 size={14} /> Sponsor Link
                        </button>
                      </div>
                    </div>
                    <p className="mt-4 text-[13px] text-emerald-900/70 leading-relaxed">
                      Invite other co-owners using your link. You&rsquo;ll earn up to <span className="font-semibold text-emerald-900">15% commissions on Level 1</span> and up to <span className="font-semibold text-emerald-900">7 Levels of referral bonuses!</span>
                    </p>
                  </div>

                  {/* Commission History (Structure tab only) */}
                  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="font-serif text-emerald-950 text-xl">Commission History</h3>
                      <span className="inline-flex items-center gap-1.5 text-emerald-800 text-sm">
                        <TrendingUp size={14} /> Total Earned: <span className="font-semibold text-emerald-950">${commissions.total_earned.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                      </span>
                    </div>

                    {/* Level Summary */}
                    <div className="mt-4 rounded-2xl border border-emerald-900/15 bg-white/70 overflow-hidden">
                      <div className="grid grid-cols-12 px-4 py-3 border-b border-emerald-900/10 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/60">
                        <div className="col-span-3">Level</div>
                        <div className="col-span-3">Rate</div>
                        <div className="col-span-3">Referrals</div>
                        <div className="col-span-3 text-right">Earned</div>
                      </div>
                      {commissions.summary.map((lvl) => (
                        <div
                          key={lvl.level}
                          data-testid={`commission-level-${lvl.level}`}
                          className="grid grid-cols-12 px-4 py-3 border-b border-emerald-900/10 last:border-b-0 items-center text-[13.5px] text-emerald-950"
                        >
                          <div className="col-span-3">
                            <span className="inline-flex items-center gap-2">
                              <span className="h-6 w-6 rounded-md bg-emerald-100 border border-emerald-700/30 text-emerald-800 text-[11px] font-bold flex items-center justify-center">L{lvl.level}</span>
                              Level {lvl.level}
                            </span>
                          </div>
                          <div className="col-span-3 text-emerald-800 font-medium">{lvl.rate_pct}%</div>
                          <div className="col-span-3">{lvl.count}</div>
                          <div className="col-span-3 text-right font-semibold">${lvl.total_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                        </div>
                      ))}
                    </div>

                    {/* Transaction list */}
                    <div className="mt-4">
                      {commissions.items.length === 0 ? (
                        <div className="rounded-xl bg-emerald-950/[0.03] border border-dashed border-emerald-900/15 px-4 py-6 text-center text-sm text-emerald-900/60">
                          No commission earnings yet. Invite co-owners with your sponsor link to start earning up to 7 levels of referral bonuses.
                        </div>
                      ) : (
                        <div className="rounded-xl border border-emerald-900/15 bg-white/70 overflow-hidden">
                          <div className="grid grid-cols-12 px-4 py-3 border-b border-emerald-900/10 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/60">
                            <div className="col-span-3">Date</div>
                            <div className="col-span-4">From</div>
                            <div className="col-span-2">Level</div>
                            <div className="col-span-3 text-right">Amount</div>
                          </div>
                          {commissions.items.map((it) => (
                            <div key={it.id} className="grid grid-cols-12 px-4 py-3 border-b border-emerald-900/10 last:border-b-0 items-center text-[13.5px] text-emerald-950">
                              <div className="col-span-3 text-emerald-900/70">{new Date(it.created_at).toLocaleDateString()}</div>
                              <div className="col-span-4">{it.from_user_email || it.from_user_id}</div>
                              <div className="col-span-2">
                                <span className="inline-flex items-center rounded-md bg-emerald-100 border border-emerald-700/30 text-emerald-800 text-[11px] font-bold px-2 py-0.5">L{it.level}</span>
                              </div>
                              <div className="col-span-3 text-right font-semibold text-emerald-800">${Number(it.amount).toFixed(2)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Purchase History (moved to Investments module) */}
          </div>
        </div>
      </div>
    </section>
  );
};

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-xl px-5 py-2.5 text-sm font-medium transition ${active ? 'bg-emerald-950 text-emerald-50 shadow-sm' : 'bg-emerald-100/60 text-emerald-900 hover:bg-emerald-100'}`}
  >
    {children}
  </button>
);

export default Dashboard;
